import { useEffect, useState } from 'react'
import { logError } from 'lib/logging'
import { GPT_MODELS, type Model } from './types'

/**
 * The GPT models the extraction pipeline can run on. Fetches the authoritative,
 * up-to-date list from the backend (which should proxy OpenAI's models endpoint and
 * filter to `gpt-*`), falling back to the static GPT_MODELS list until that endpoint
 * exists. Non-GPT models are filtered out regardless. See plan §8 (backend dep).
 */
export function useAvailableModels(): { models: Model[]; loading: boolean } {
  const [models, setModels] = useState<Model[]>(GPT_MODELS)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    window
      .fetch(`/api/ops/available-models`, { credentials: `include` })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`unavailable`))))
      .then((data) => {
        const fetched: unknown = data?.models
        if (active && Array.isArray(fetched)) {
          const gpt = fetched.filter((model): model is string => typeof model === `string` && model.startsWith(`gpt`))
          if (gpt.length) setModels(gpt)
        }
      })
      .catch((error) => logError(`available-models fetch failed; using fallback list`, error))
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  return { models, loading }
}
